import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { promptApi } from '@/services/promptApi';
import { usePaginatedList } from '@/hooks';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { MessageSquare, Eye, Play } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { PromptSummary } from '@/types/prompt';

export const PromptListPage: React.FC = () => {
  const fetchFn = useMemo(
    () => (params: { page: number; size: number }) =>
      promptApi.list({
        page: params.page,
        size: params.size,
        sortBy: 'updatedAt',
        sortDirection: 'desc',
      }),
    []
  );

  const {
    data: prompts,
    isLoading,
    error,
    pagination,
    setPage,
    refetch,
  } = usePaginatedList<PromptSummary>({
    fetchFn,
    entityName: 'prompts',
    suppress404Errors: true,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Prompt Library</h1>
          <p className="text-muted-foreground mt-2">
            View and test prompt templates from the prompt service
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Prompts ({pagination.totalElements})</CardTitle>
          <CardDescription>List of prompt templates. Open a prompt to view details and run a test.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading prompts..." />
          ) : error || prompts.length === 0 ? (
            <EmptyState
              title="No prompts found"
              description={
                error
                  ? 'Unable to load prompts. Check that the prompt service is running and try again.'
                  : 'No prompt templates are available yet.'
              }
              icon={MessageSquare}
              actions={[
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline',
                },
              ]}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prompts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        {p.name ?? p.key ?? p.id}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{p.key ?? '—'}</TableCell>
                      <TableCell className="max-w-xs truncate text-muted-foreground">
                        {p.description ?? '—'}
                      </TableCell>
                      <TableCell>{p.updatedAt ? formatDate(p.updatedAt) : '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild title="View">
                            <Link to={`/admin/prompts/${encodeURIComponent(p.key ?? p.id)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="default"
                            size="icon"
                            asChild
                            title="Test prompt"
                          >
                            <Link to={`/admin/prompts/${encodeURIComponent(p.key ?? p.id)}?test=1`}>
                              <Play className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min(pagination.totalPages - 1, pagination.page + 1))}
                      disabled={pagination.page >= pagination.totalPages - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PromptListPage;
